import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface CalendarItem {
  id: string;
  name: string;
  color: string;
  visible: boolean;
  events: CalendarEvent[];
}

export interface CalendarEvent {
  uuid?: string;
  title?: string;
  description?: string;
  color?: string;
  visible?: boolean;
  start?: string;
  end?: string;
  created_at?: string;
  updated_at?: string;
  employees?: User[];
  address?: string;
  link?: string;
}

export interface User {
  uuid: string;
  name: string;
  image: string;
  email: string;
}

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  private readonly STORAGE_KEY = 'fara_chat_calendars';
  private calendarsSubject = new BehaviorSubject<CalendarItem[]>([]);
  public calendars$ = this.calendarsSubject.asObservable();

  constructor() {
    this.initializeCalendars();
  }

  private initializeCalendars(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      try {
        const calendars = JSON.parse(stored);
        this.calendarsSubject.next(calendars);
      } catch (error) {
        console.error('Error parsing stored calendars:', error);
        this.createDefaultCalendars();
      }
    } else {
      this.createDefaultCalendars();
    }
  }

  private createDefaultCalendars(): void {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;
    const yesterdayStr = `${yyyy}-${mm}-${Number(dd) - 1}`;
    const tomorrowStr = `${yyyy}-${mm}-${Number(dd) + 1}`;

    const defaultCalendars: CalendarItem[] = [
      {
        id: '1',
        name: 'Personal Calendar',
        color: '#f4cce0',
        visible: true,
        events: [
          {
            uuid: '1',
            title: 'Team Meeting',
            description: 'Discuss the upcoming sprint and tasks',
            color: '#6ca12b',
            visible: true,
            start: `${todayStr}T09:00:00`,
            end: `${todayStr}T10:00:00`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            employees: [
              {
                uuid: 'user-001',
                name: 'John Smith',
                image: 'https://i.pravatar.cc/150?img=1',
                email: 'john.smith@company.com',
              },
              {
                uuid: 'user-002',
                name: 'Sarah Johnson',
                image: 'https://i.pravatar.cc/150?img=2',
                email: 'sarah.johnson@company.com',
              },
              {
                uuid: 'user-003',
                name: 'Mike Wilson',
                image: 'https://i.pravatar.cc/150?img=3',
                email: 'mike.wilson@company.com',
              },
            ],
            address: 'Room 301, HQ Building',
            link: 'https://meet.example.com/team-meeting',
          },
          {
            uuid: '2',
            title: 'Design Review',
            description: 'Review new UI components for dashboard',
            color: '#ff8c42',
            visible: true,
            start: `${todayStr}T12:00:00`,
            end: `${todayStr}T13:00:00`,
            employees: [
              {
                uuid: 'user-002',
                name: 'Sarah Johnson',
                image: 'https://i.pravatar.cc/150?img=2',
                email: 'sarah.johnson@company.com',
              },
              {
                uuid: 'user-001',
                name: 'John Smith',
                image: 'https://i.pravatar.cc/150?img=1',
                email: 'john.smith@company.com',
              },
            ],
            address: 'Zoom',
            link: 'https://zoom.us/design-review',
          },
        ],
      },
      {
        id: '2',
        name: 'Work Calendar',
        color: '#ffe0b2',
        visible: true,
        events: [
          {
            uuid: '3',
            title: 'Daily Standup',
            start: `${yesterdayStr}T08:30:00`,
            end: `${yesterdayStr}T09:00:00`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          {
            uuid: '4',
            title: 'Project Planning',
            start: `${yesterdayStr}T10:15:00`,
            end: `${yesterdayStr}T11:30:00`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          {
            uuid: '5',
            title: 'Code Review',
            start: `${todayStr}T17:15:00`,
            end: `${todayStr}T18:00:00`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ],
      },
      {
        id: '3',
        name: 'Holidays',
        color: '#d9c7f2',
        visible: true,
        events: [
          {
            uuid: '6',
            title: 'Marketing Sync',
            start: `${todayStr}T10:30:00`,
            end: `${todayStr}T11:00:00`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          {
            uuid: '7',
            title: 'Backend Debugging',
            start: `${tomorrowStr}T11:00:00`,
            end: `${tomorrowStr}T12:30:00`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ],
      },
    ];

    // Add random events for current week
    this.addRandomWeekEvents(defaultCalendars);

    this.saveCalendars(defaultCalendars);
    this.calendarsSubject.next(defaultCalendars);
  }

  private addRandomWeekEvents(calendars: CalendarItem[]): void {
    const today = new Date();
    const currentWeek = this.getCurrentWeekDates(today);

    const eventTitles = [
      'Morning Standup',
      'Client Meeting',
      'Lunch Break',
      'Team Sync',
      'Project Review',
      'Code Review',
      'Planning Session',
      'Demo Day',
      'Retrospective',
      'Training Session',
      'One-on-One',
      'Workshop',
      'Conference Call',
      'Sprint Planning',
      'Design Review',
    ];

    calendars.forEach((calendar) => {
      // Add 2-4 random events for the week
      const eventCount = Math.floor(Math.random() * 3) + 2;

      for (let i = 0; i < eventCount; i++) {
        const randomDate = currentWeek[Math.floor(Math.random() * currentWeek.length)];
        const randomTitle = eventTitles[Math.floor(Math.random() * eventTitles.length)];
        const startHour = Math.floor(Math.random() * 8) + 9; // 9 AM to 5 PM
        const duration = Math.floor(Math.random() * 3) + 1; // 1-3 hours

        const event: CalendarEvent = {
          uuid: `random-${calendar.id}-${i}-${Date.now()}`,
          title: randomTitle,
          description: `Random event for ${calendar.name}`,
          visible: true,
          start: `${randomDate}T${startHour.toString().padStart(2, '0')}:00:00`,
          end: `${randomDate}T${(startHour + duration).toString().padStart(2, '0')}:00:00`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        calendar.events.push(event);
      }
    });
  }

  private getCurrentWeekDates(date: Date): string[] {
    const week = [];
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay()); // Start from Sunday

    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startOfWeek);
      currentDate.setDate(startOfWeek.getDate() + i);
      const yyyy = currentDate.getFullYear();
      const mm = String(currentDate.getMonth() + 1).padStart(2, '0');
      const dd = String(currentDate.getDate()).padStart(2, '0');
      week.push(`${yyyy}-${mm}-${dd}`);
    }

    return week;
  }

  private saveCalendars(calendars: CalendarItem[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(calendars));
  }

  getCalendars(): CalendarItem[] {
    return this.calendarsSubject.value;
  }

  getCalendarById(id: string): CalendarItem | undefined {
    return this.calendarsSubject.value.find((cal) => cal.id === id);
  }

  addCalendar(calendar: Omit<CalendarItem, 'id' | 'events'>): CalendarItem {
    const newCalendar: CalendarItem = {
      ...calendar,
      id: Date.now().toString(),
      events: [],
    };

    const calendars = [...this.calendarsSubject.value, newCalendar];
    this.saveCalendars(calendars);
    this.calendarsSubject.next(calendars);

    return newCalendar;
  }

  updateCalendar(id: string, updates: Partial<CalendarItem>): boolean {
    const calendars = this.calendarsSubject.value.map((cal) =>
      cal.id === id ? { ...cal, ...updates } : cal,
    );

    this.saveCalendars(calendars);
    this.calendarsSubject.next(calendars);

    return true;
  }

  deleteCalendar(id: string): boolean {
    const calendars = this.calendarsSubject.value.filter((cal) => cal.id !== id);
    this.saveCalendars(calendars);
    this.calendarsSubject.next(calendars);

    return true;
  }

  addEventToCalendar(calendarId: string, event: Omit<CalendarEvent, 'uuid'>): CalendarEvent {
    const newEvent: CalendarEvent = {
      ...event,
      uuid: Date.now().toString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const calendars = this.calendarsSubject.value.map((cal) => {
      if (cal.id === calendarId) {
        return { ...cal, events: [...cal.events, newEvent] };
      }
      return cal;
    });

    this.saveCalendars(calendars);
    this.calendarsSubject.next(calendars);

    return newEvent;
  }

  updateEvent(calendarId: string, eventId: string, updates: Partial<CalendarEvent>): boolean {
    const calendars = this.calendarsSubject.value.map((cal) => {
      if (cal.id === calendarId) {
        return {
          ...cal,
          events: cal.events.map((event) =>
            event.uuid === eventId
              ? { ...event, ...updates, updated_at: new Date().toISOString() }
              : event,
          ),
        };
      }
      return cal;
    });

    this.saveCalendars(calendars);
    this.calendarsSubject.next(calendars);

    return true;
  }

  deleteEvent(calendarId: string, eventId: string): boolean {
    const calendars = this.calendarsSubject.value.map((cal) => {
      if (cal.id === calendarId) {
        return {
          ...cal,
          events: cal.events.filter((event) => event.uuid !== eventId),
        };
      }
      return cal;
    });

    this.saveCalendars(calendars);
    this.calendarsSubject.next(calendars);

    return true;
  }

  getAllEvents(searchQuery?: string): any[] {
    let events = this.calendarsSubject.value
      .filter((cal) => cal.visible)
      .flatMap((cal) =>
        cal.events.map((event) => ({
          title: event.title,
          start: event.start,
          end: event.end,
          backgroundColor: cal.color,
          borderColor: cal.color,
          textColor: '#0f0f0f',
          extendedProps: event,
        })),
      );

    // Apply search filter if search query exists
    if (searchQuery && searchQuery.trim()) {
      events = events.filter((event) =>
        event.title?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    return events;
  }

  getEventsByCalendar(calendarId: string): CalendarEvent[] {
    const calendar = this.getCalendarById(calendarId);
    return calendar ? calendar.events : [];
  }

  toggleCalendarVisibility(id: string): void {
    const calendars = this.calendarsSubject.value.map((cal) =>
      cal.id === id ? { ...cal, visible: !cal.visible } : cal,
    );

    this.saveCalendars(calendars);
    this.calendarsSubject.next(calendars);
  }

  getFakeUsers(): User[] {
    return [
      {
        uuid: 'user-001',
        name: 'John Smith',
        image: 'https://i.pravatar.cc/150?img=1',
        email: 'john.smith@company.com',
      },
      {
        uuid: 'user-002',
        name: 'Sarah Johnson',
        image: 'https://i.pravatar.cc/150?img=2',
        email: 'sarah.johnson@company.com',
      },
      {
        uuid: 'user-003',
        name: 'Mike Wilson',
        image: 'https://i.pravatar.cc/150?img=3',
        email: 'mike.wilson@company.com',
      },
      {
        uuid: 'user-004',
        name: 'Emily Davis',
        image: 'https://i.pravatar.cc/150?img=4',
        email: 'emily.davis@company.com',
      },
      {
        uuid: 'user-005',
        name: 'David Brown',
        image: 'https://i.pravatar.cc/150?img=5',
        email: 'david.brown@company.com',
      },
      {
        uuid: 'user-006',
        name: 'Lisa Anderson',
        image: 'https://i.pravatar.cc/150?img=6',
        email: 'lisa.anderson@company.com',
      },
      {
        uuid: 'user-007',
        name: 'Alex Rodriguez',
        image: 'https://i.pravatar.cc/150?img=7',
        email: 'alex.rodriguez@company.com',
      },
      {
        uuid: 'user-008',
        name: 'Maria Garcia',
        image: 'https://i.pravatar.cc/150?img=8',
        email: 'maria.garcia@company.com',
      },
      {
        uuid: 'user-009',
        name: 'James Wilson',
        image: 'https://i.pravatar.cc/150?img=9',
        email: 'james.wilson@company.com',
      },
      {
        uuid: 'user-010',
        name: 'Jennifer Lee',
        image: 'https://i.pravatar.cc/150?img=10',
        email: 'jennifer.lee@company.com',
      },
    ];
  }
}
